import { Injectable } from '@angular/core';

import { GisToolsApi } from '../api/gis-tools-api';
import { IRMSOffset } from '../types';

@Injectable({
    providedIn: 'root'
})
export class GisToolsService {

    constructor(private readonly gisToolsApi: GisToolsApi) { }

    async getDistricts(ids: number[]) {
        const districts = await this.gisToolsApi.getDistricts(ids);
        return this.orderBy(districts, 'districtNumber');
    }

    async getCounties(ids: number[]) {
        const counties = await this.gisToolsApi.getCounties(ids);
        return this.orderBy(counties, 'countyCode');
    }

    async getCountiesByDistricts(districtNumbers: string[]) {
        const counties = await this.gisToolsApi.getCountiesByDistricts(districtNumbers);

        return this.orderBy(counties, 'countyCode');
    }

    async getAllDistricts(props: string) {
        const districts = await this.gisToolsApi.getAllDistricts(props);
        return this.orderBy(districts, 'districtNumber');
    }

    async getAllCounties(props: string, sortProp: string = 'countyCode') {
        const counties = await this.gisToolsApi.getAllCounties(props);
        return this.orderBy(counties, sortProp);
    }

    async getInterstatesByNetworkIndicator(networkIndicator: string = 'Y', sortProp: string = 'stateRouteNumber') {
        const interstates = await this.gisToolsApi.getInterstatesByNetworkIndicator([networkIndicator]);
        return this.orderBy(interstates, sortProp);
    }

    async getMunicipalitiesByCounty(countyCode: string, props: string) {
        const municipalities = await this.gisToolsApi.getMunicipalitiesByCounty(countyCode, props);
        return this.orderBy(municipalities, 'municipalityCode');
    }

    async getAllMunicipalities(props: string) {
        const municipalities = await this.gisToolsApi.getAllMunicipalities(props);
        return this.orderBy(municipalities, 'municipalityCode');
    }
    async getAllSchoolDistricts(props: string) {
        const schoolDistricts = await this.gisToolsApi.getAllSchoolDistricts(props);
        return this.orderBy(schoolDistricts, 'schoolDistrictName');
    }
    async getAllZipCodes(props: string) {
        const zipCodes = await this.gisToolsApi.getAllZipCodes(props);
        return this.orderBy(zipCodes, 'zip');
    }

    async getAllPlanningPartners(props: string) {
        const planningPartners = await this.gisToolsApi.getAllPlanningPartners(props);
        return this.orderBy(planningPartners, 'planningPartnerShortName');
    }

    async getAllHouseDistricts(props: string) {
        const houseDistricts = await this.gisToolsApi.getAllHouseDistricts(props);
        houseDistricts.forEach(h => h.houseDistrictNumber = h.legislativeDistrictNumber);
        return this.orderBy(houseDistricts, 'legislativeDistrictNumber');
    }

    async getAllSenateDistricts(props: string) {
        const senateDistricts = await this.gisToolsApi.getAllSenateDistricts(props);
        senateDistricts.forEach(s => s.senateDistrictNumber = s.legislativeDistrictNumber);
        return this.orderBy(senateDistricts, 'legislativeDistrictNumber');
    }

    async getAllCongressionalDistricts(props: string) {
        const congressionalDistricts = await this.gisToolsApi.getAllCongressionalDistricts(props);
        congressionalDistricts.forEach(s => s.congressionalDistrictNumber = s.legislativeDistrictNumber);
        return this.orderBy(congressionalDistricts, 'legislativeDistrictNumber');
    }

    async getAllStateRoutes(props: string) {
        const stateRoutes = await this.gisToolsApi.getAllStateRoutes(props);
        return this.orderBy(stateRoutes, 'stateRouteNumber');
    }

    async getStateRoutesByCounty(countyCode: string, props: string) {
        const stateRoutes = await this.gisToolsApi.getStateRoutesByCounty(countyCode, props);
        return this.orderBy(stateRoutes, 'stateRouteNumber');
    }

    async getStateRoutesByCountyAndJuris(countyCode: string, juris: number[], props: string) {
        const stateRoutes = await this.gisToolsApi.getStateRoutesByCountyJuris(countyCode, juris, props);
        return this.orderBy(stateRoutes, 'stateRouteNumber');
    }

    async getStateRoutesByCountyJuris(countyCode: string, jurisValues: number[], props: string) {
        const stateRoutes = await this.gisToolsApi.getStateRoutesByCountyJuris(countyCode, jurisValues, props);
        return this.orderBy(stateRoutes, 'stateRouteNumber');
    }

    async getAllSegments(props: string) {
        const segments = await this.gisToolsApi.getAllSegments(props);
        return this.orderBy(segments, 'segmentNumber');
    }

    async getSegmentsByCountyAndRoute(countyCode: string, routeNumber: string, props: string) {
        const segments = await this.gisToolsApi.getSegmentsByCountyAndRoute(countyCode, routeNumber, props);
        return this.orderBy(segments, 'segmentNumber');
    }

    async getSegmentsNearLatLong(latitude, longitude, tolerance) {
        const segments = await this.gisToolsApi.getSegmentsNearLatLong(latitude, longitude, tolerance);
        return segments;
    }

    async getOffsetForCRSLatLong(lat: number, long: number, county: string, route: string, buffer: number, props: string) {
        if (!props.includes('distance')) {
            props = [...props.split(','), 'distance'].join(',');
        }
        const segments = await this.gisToolsApi.getOffsetForCRSLatLong(lat, long, county, route, buffer, props);
        return this.orderBy<IRMSOffset>(segments, 'distance');
    }

    async getAllColumnIdentifiers(props: string) {
        return this.gisToolsApi.getAllColumnIdentifiers(props);
    }

    async getColumns(layerId, props) {
        const result = await this.gisToolsApi.getColumns(layerId, props);
        if (result.length > 0) {
            return result[0].columns;
        }
    }

    async getOffsetForLatLong(lat, long, buffer, props) {
        return await this.gisToolsApi.getOffsetForLatLong(lat, long, buffer, props);
    }

    async getNearbyLocalIntersections(lat, long, buffer, props) {
        return await this.gisToolsApi.getNearbyLocalIntersections(lat, long, buffer, props);
    }

    async getLatLongForOffset(countyCode, stateRouteNumber, segmentNumber, offset) {
        const result = await this.gisToolsApi.getLatLongForOffset(countyCode, stateRouteNumber, segmentNumber, offset);
        if (result && result[0]) {
            return result[0].latLong;
        }
    }

    async getFeaturesByObjectId(pluralObjectName: string, props: string, objectIds: number[]) {
        return await this.gisToolsApi.getFeaturesByObjectId(pluralObjectName, props, objectIds);
    }

    async getRailCrossing(crossingId, props) {
        const result = await this.gisToolsApi.getRailCrossing(crossingId, props);
        return result && result.length === 1 ? result[0] : null;
    }

    orderBy<T = any>(arr: T[], prop: string) {
        if (!arr) { return arr; }
        return arr.sort((a, b) => (a[prop] > b[prop]) ? 1 : ((b[prop] > a[prop]) ? -1 : 0));
    }
}
